import React, { useEffect, useMemo, useState } from "react";
import { Formik } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import {
  createUnreceivedGoods,
  getNomenclature,
} from "../../redux/api/receipt.api";
import { dateForDatabase } from "../../utils/dateAndTime";

import ContentCard from "../../components/misc/ContentCard";
import TextInput from "../../components/misc/TextInput";
import Dropdown, { DropdownValue } from "../../components/misc/Dropdown";
import CustomButton from "../../components/CustomButton";
import DatePicker from "../../components/misc/DatePicker";
import Checkbox from "../../components/misc/Checkbox";

type FormValues = {
  furnizor: string;
  dataDesc: Date | null;
  nrAuto: string;
  bonTemperatura: boolean;
  nrPaleti: string;
  nrColete: string;
  observatii: string;
};

type InvoiceRow = {
  factura: string;
  dataFactura: Date | null;
  _key?: string;
};

const initialValues: FormValues = {
  furnizor: "",
  dataDesc: null,
  nrAuto: "",
  bonTemperatura: false,
  nrPaleti: "",
  nrColete: "",
  observatii: "",
};

const schema = Yup.object({
  furnizor: Yup.string().trim().required("Camp obligatoriu"),
  dataDesc: Yup.date().nullable().required("Camp obligatoriu"),
  nrAuto: Yup.string().trim().required("Camp obligatoriu"),
  bonTemperatura: Yup.boolean().required("Camp obligatoriu"),
  nrPaleti: Yup.number()
    .transform((v, o) => (o === "" ? null : v))
    .nullable()
    .min(0, "Nu poate fi negativ"),
  nrColete: Yup.number()
    .transform((v, o) => (o === "" ? null : v))
    .nullable()
    .min(0, "Nu poate fi negativ"),
  observatii: Yup.string().trim().notRequired(),
}).test(
  "paleti-sau-colete",
  "Completați nr. paleți sau nr. colete.",
  (values: any) => {
    const hasPaleti =
      values?.nrPaleti !== null &&
      values?.nrPaleti !== undefined &&
      values?.nrPaleti !== "";
    const hasColete =
      values?.nrColete !== null &&
      values?.nrColete !== undefined &&
      values?.nrColete !== "";
    return hasPaleti || hasColete;
  },
);

export default function CreateUnreceivedGoods() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { provider } = useAppSelector((s) => s.receipt.nomenclature);

  const [selectedProvider, setSelectedProvider] =
    useState<DropdownValue | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [invoices, setInvoices] = useState<InvoiceRow[]>([
    {
      factura: "",
      dataFactura: null,
      _key: crypto.randomUUID?.() || String(Math.random()),
    },
  ]);

  const invoicesValid = useMemo(
    () =>
      invoices.every((r) => {
        const f = r.factura.trim();
        const d = r.dataFactura;
        return (f === "" && !d) || (f !== "" && !!d);
      }),
    [invoices],
  );
  const [showInvoicesError, setShowInvoicesError] = useState(false);

  useEffect(() => {
    dispatch(getNomenclature());
  }, [dispatch]);

  const providersOptions: DropdownValue[] = useMemo(
    () =>
      (provider || []).map(
        (p: { idFurnizor: number; numeFurnizor: string }) => ({
          id: p.idFurnizor,
          label: p.numeFurnizor,
        }),
      ),
    [provider],
  );

  const handleCancel = () => navigate(-1);

  const addInvoiceRow = () => {
    setInvoices((prev) => [
      ...prev,
      {
        factura: "",
        dataFactura: null,
        _key: crypto.randomUUID?.() || String(Math.random()),
      },
    ]);
  };

  const removeInvoiceRow = (idx: number) => {
    setInvoices((prev) => prev.filter((_, i) => i !== idx));
  };

  const updateInvoiceField = (
    idx: number,
    field: keyof InvoiceRow,
    value: any,
  ) => {
    setInvoices((prev) =>
      prev.map((row, i) => (i === idx ? { ...row, [field]: value } : row)),
    );
    if (showInvoicesError) setShowInvoicesError(false);
  };

  const handleSubmitForm = async (values: FormValues) => {
    if (!invoicesValid) {
      setShowInvoicesError(true);
      return;
    }
    if (!selectedProvider?.id) return;

    setSubmitting(true);

    const payload = {
      furnizor: values.furnizor,
      furnizorId: selectedProvider.id,
      dataDesc: dateForDatabase(values.dataDesc),
      nrAuto: values.nrAuto,
      bonTemperatura: !!values.bonTemperatura,
      nrPaleti: values.nrPaleti === "" ? null : Number(values.nrPaleti),
      nrColete: values.nrColete === "" ? null : Number(values.nrColete),
      observatii: values.observatii,
      invoices: invoices
        .filter((inv) => inv.factura.trim() || inv.dataFactura)
        .map((inv) => ({
          factura: inv.factura.trim(),
          dataFactura: dateForDatabase(inv.dataFactura),
        })),
    };

    const res: any = await dispatch(createUnreceivedGoods(payload));
    setSubmitting(false);
    if (!res?.error) navigate(-1);
  };

  return (
    <ContentCard cardTitle="Creează marfa nereceptionata">
      <Formik
        initialValues={initialValues}
        validationSchema={schema}
        onSubmit={handleSubmitForm}
        validateOnMount>
        {({
          errors,
          touched,
          getFieldProps,
          handleSubmit,
          isValid,
          setFieldValue,
          values,
        }) => {
          const hasPaleti =
            values.nrPaleti !== "" &&
            values.nrPaleti !== null &&
            values.nrPaleti !== undefined;
          const hasColete =
            values.nrColete !== "" &&
            values.nrColete !== null &&
            values.nrColete !== undefined;

          const canSubmit =
            isValid &&
            !submitting &&
            !!selectedProvider?.id &&
            invoicesValid &&
            (hasPaleti || hasColete);

          return (
            <form onSubmit={handleSubmit} noValidate>
              <div className="flex">
                <Dropdown
                  className="mb-4 mr-2"
                  title="Furnizor"
                  data={providersOptions}
                  singleValue={selectedProvider}
                  setSingleValue={(val) => {
                    setSelectedProvider(val);
                    setFieldValue("furnizor", val?.label ?? "");
                  }}
                />

                <DatePicker
                  label="Data desc"
                  className="mb-4 mr-2"
                  value={values.dataDesc}
                  disabled={submitting}
                  handleChangeDate={(value) => setFieldValue("dataDesc", value)}
                />
              </div>

              <div className="flex">
                <TextInput
                  className="mb-4 mr-2"
                  label="Nr. auto"
                  name="nrAuto"
                  error={errors["nrAuto"]}
                  touched={touched["nrAuto"]}
                  inputProps={getFieldProps("nrAuto")}
                />

                <TextInput
                  className="mb-4 mr-2"
                  label="Nr. Paleți"
                  name="nrPaleti"
                  error={errors["nrPaleti"]}
                  touched={touched["nrPaleti"]}
                  inputProps={{
                    ...getFieldProps("nrPaleti"),
                    type: "number",
                    min: 0,
                  }}
                />
                <TextInput
                  className="mb-4 mr-2"
                  label="Nr. Colete"
                  name="nrColete"
                  error={errors["nrColete"]}
                  touched={touched["nrColete"]}
                  inputProps={{
                    ...getFieldProps("nrColete"),
                    type: "number",
                    min: 0,
                  }}
                />
              </div>

              <ContentCard>
                <div className="flex items-center justify-between mb-2">
                  <h6 className="mb-0">Facturi</h6>
                  <CustomButton
                    variant="outlined"
                    icon="ADD"
                    tooltipTitle="Adauga o factura in formular."
                    onClick={addInvoiceRow}
                    disabled={submitting}>
                    Adauga factura
                  </CustomButton>
                </div>

                {invoices.map((row, idx) => (
                  <div key={row._key || idx} className="flex items-center mb-3">
                    <TextInput
                      className="mr-2"
                      label={`Factura #${idx + 1}`}
                      name={`factura_${idx}`}
                      inputProps={{
                        value: row.factura,
                        onChange: (e: any) =>
                          updateInvoiceField(idx, "factura", e.target.value),
                      }}
                    />
                    <DatePicker
                      label="Data factură"
                      className="mr-2"
                      value={row.dataFactura}
                      disabled={submitting}
                      handleChangeDate={(value) =>
                        updateInvoiceField(idx, "dataFactura", value)
                      }
                      maxDate={new Date()}
                    />

                    <CustomButton
                      variant="outlined"
                      color="error"
                      icon="DELETE"
                      tooltipTitle="Elimina factura din formular."
                      className="ml-1"
                      onClick={() => removeInvoiceRow(idx)}
                      disabled={submitting || invoices.length === 1}>
                      Sterge factura
                    </CustomButton>
                  </div>
                ))}

                {showInvoicesError && !invoicesValid && (
                  <div className="text-danger text-sm">
                    Dacă completați factura sau data, completați-le pe ambele
                    pentru fiecare rând.
                  </div>
                )}
              </ContentCard>

              <div className="mb-4 mr-4 flex items-center">
                <Checkbox
                  label="Bon temperatură"
                  checked={values.bonTemperatura}
                  onChange={(value) => setFieldValue("bonTemperatura", value)}
                  disabled={submitting}
                  labelAlign="left"
                />
              </div>

              <TextInput
                className="mb-4"
                label="Observații"
                name="observatii"
                error={errors["observatii"]}
                touched={touched["observatii"]}
                inputProps={getFieldProps("observatii")}
                type="textarea"
              />

              <div className="flex justify-between">
                <CustomButton variant="outlined" onClick={handleCancel}>
                  Anulează
                </CustomButton>
                <CustomButton
                  type="submit"
                  variant="contained"
                  loading={submitting}
                  disabled={!canSubmit}>
                  Salvează
                </CustomButton>
              </div>
            </form>
          );
        }}
      </Formik>
    </ContentCard>
  );
}
